import React from 'react';
import {
  PieChart, Pie, Cell,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useLanguage } from '../../context/LanguageContext';
import { PageHeader } from '../../lib/commonUtils';
import useAppStore from '../../store/appStore';
import {
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  PhoneCall,
  CheckCircle,
  Clock,
  Activity,
  Bot,
} from 'lucide-react';

// Defined outside Analytics so React never re-creates it as a new component type on each render
const KPICard = ({ title, value, change, isPositive, icon: Icon, subtitle, gradient }) => (
  <div className="controlled-glass rounded-[28px] p-6 flex flex-col h-full inner-highlight group hover:border-white/20 transition-all duration-300 relative overflow-hidden">
    <div className={`absolute -right-4 -top-4 w-24 h-24 blur-3xl opacity-10 group-hover:opacity-20 transition-opacity bg-gradient-to-br ${gradient}`} />

    <div className="flex items-center gap-4 mb-8 relative z-10">
      <div className="p-2.5 rounded-xl bg-white/5 border border-white/10 group-hover:bg-white/10 transition-colors shadow-lg shadow-black/20">
        <Icon className="h-5 w-5 text-white/70 group-hover:text-white transition-colors" />
      </div>
      <span className="text-[11px] font-bold text-white/40 uppercase tracking-[0.15em] leading-tight flex-1 group-hover:text-white/60 transition-colors">{title}</span>
    </div>

    <div className="mt-auto space-y-4 relative z-10">
      <div className="flex items-end justify-between gap-4">
        <h3 className="text-3xl font-bold text-white tracking-tight leading-none drop-shadow-sm">{value}</h3>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border shrink-0 ${isPositive
          ? 'bg-green-500/10 text-green-400 border-green-500/20 shadow-[0_0_10px_rgba(34,197,94,0.1)]'
          : 'bg-red-500/10 text-red-400 border-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.1)]'
        }`}>
          {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {change}%
        </div>
      </div>

      {subtitle && (
        <div className="pt-4 border-t border-white/5">
          <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest italic leading-snug group-hover:text-white/50 transition-colors">
            {subtitle}
          </p>
        </div>
      )}
    </div>
  </div>
);

const Analytics = () => {
  const { t } = useLanguage();
  const { dashboardData, dashboardLoading, fetchDashboardAnalysis } = useAppStore((s) => ({
    dashboardData: s.dashboardData,
    dashboardLoading: s.dashboardLoading,
    fetchDashboardAnalysis: s.fetchDashboardAnalysis,
  }));

  React.useEffect(() => {
    fetchDashboardAnalysis();
  }, [fetchDashboardAnalysis]);

  const data = dashboardData;
  const loading = dashboardLoading && !dashboardData;

  const dash = data?.dashboard || {};
  const growth = dash.growth_vs_last_month || {};
  const rawAgents = data?.your_ai_agents?.test_speed || [];

  const totalCalls = dash.total_calls ?? 0;
  const successRate = dash.success_rate ?? 0;
  const avgDurationSecs = dash.avg_call_duration ?? 0;

  const totalHours = totalCalls > 0 ? (totalCalls * avgDurationSecs / 3600).toFixed(1) : '0';
  const minutesUsed = Math.round(totalCalls * avgDurationSecs / 60);
  const successfulCalls = Math.round(totalCalls * successRate / 100);

  const failedPct = Math.max(0, Math.round(100 - successRate));
  const successRateData = [
    { name: t('successful'), value: Math.round(successRate), color: '#4f6ef7' },
    { name: t('failed'), value: failedPct, color: '#ccd6f6' },
  ];

  const agentStats = rawAgents.map((a) => ({
    name: a.agent_name,
    calls: a.calls ?? 0,
    rate: a.success_rate ?? 0,
    status: a.active ? 'active' : 'inactive',
  }));

  return (
    <div className="space-y-6 pb-12">

      <PageHeader title={t('analytics')} subtitle={t('trackPerformance')} />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPICard
          title="Total Duration (Hours Saved)"
          value={loading ? '—' : totalHours}
          change={Math.abs(growth.avg_call_duration_percent ?? 0)}
          isPositive={(growth.avg_call_duration_percent ?? 0) >= 0}
          icon={Clock}
          subtitle="Calls handled by AI"
          gradient="from-blue-500/20 to-indigo-600/20"
        />
        <KPICard
          title="Active Agents"
          value={loading ? '—' : (dash.active_agents ?? 0)}
          change={Math.abs(growth.active_agents_growth_percent ?? 0)}
          isPositive={(growth.active_agents_growth_percent ?? 0) >= 0}
          icon={Bot}
          subtitle="Currently live agents"
          gradient="from-purple-500/20 to-fuchsia-600/20"
        />
        <KPICard
          title="Successful Calls"
          value={loading ? '—' : successfulCalls.toLocaleString()}
          change={Math.abs(growth.success_rate_percent ?? 0)}
          isPositive={(growth.success_rate_percent ?? 0) >= 0}
          icon={CheckCircle}
          subtitle="Calls with a summary"
          gradient="from-emerald-500/20 to-teal-600/20"
        />
        <KPICard
          title="Avg Call Duration"
          value={loading ? '—' : `${Math.floor(avgDurationSecs / 60)}m ${Math.round(avgDurationSecs % 60)}s`}
          change={Math.abs(growth.avg_call_duration_percent ?? 0)}
          isPositive={(growth.avg_call_duration_percent ?? 0) >= 0}
          icon={Clock}
          subtitle="Per call average"
          gradient="from-orange-500/20 to-amber-600/20"
        />
        <KPICard
          title={t('callsReceived')}
          value={loading ? '—' : totalCalls.toLocaleString()}
          change={Math.abs(growth.total_calls_percent ?? 0)}
          isPositive={(growth.total_calls_percent ?? 0) >= 0}
          icon={PhoneCall}
          subtitle={t('totalCallsMonth')}
          gradient="from-slate-500/10 to-transparent"
        />
        <KPICard
          title={t('callsHandledAI')}
          value={loading ? '—' : successfulCalls.toLocaleString()}
          change={Math.abs(growth.success_rate_percent ?? 0)}
          isPositive={(growth.success_rate_percent ?? 0) >= 0}
          icon={Bot}
          subtitle={t('managedByValoria')}
          gradient="from-indigo-500/10 to-transparent"
        />
        <KPICard
          title="Success Rate"
          value={loading ? '—' : `${Math.round(successRate)}%`}
          change={Math.abs(growth.success_rate_percent ?? 0)}
          isPositive={(growth.success_rate_percent ?? 0) >= 0}
          icon={Activity}
          subtitle="Calls with outcome"
          gradient="from-red-500/10 to-transparent"
        />
        <KPICard
          title={t('minutesUsed')}
          value={loading ? '—' : minutesUsed.toLocaleString()}
          change={Math.abs(growth.avg_call_duration_percent ?? 0)}
          isPositive={false}
          icon={Activity}
          subtitle={t('planUsageStatus')}
          gradient="from-cyan-500/10 to-transparent"
        />
      </div>

      {/* Daily Call Volume + Success Rate Pie */}
      <div className="grid grid-cols-12 gap-5 items-stretch">
        <div className="col-span-12 lg:col-span-8">
          <div className="controlled-glass rounded-[24px] p-6 h-full inner-highlight overflow-hidden">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight">{t('dailyCallVolume')}</h2>
                <p className="text-xs text-white/30 mt-1 font-medium italic">{t('callTracking30d')}</p>
              </div>
            </div>
            <div className="h-[400px] w-full flex items-center justify-center">
              <p className="text-xs font-bold text-white/20 uppercase tracking-widest italic">No call data available yet</p>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="controlled-glass rounded-[24px] p-6 h-full inner-highlight flex flex-col">
            <div className="mb-8">
              <h2 className="text-lg font-bold text-white tracking-tight">{t('successRateDistribution')}</h2>
              <p className="text-xs text-white/30 mt-1 font-medium italic">{t('callOutcomesBreakdown')}</p>
            </div>
            <div className="flex-1 flex flex-col justify-center">
              <div className="h-[240px] w-full relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={successRateData} cx="50%" cy="50%" innerRadius={75} outerRadius={95} paddingAngle={8} dataKey="value" stroke="none">
                      {successRateData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1a1f35', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-3xl font-bold text-white tracking-tight">{loading ? '—' : `${Math.round(successRate)}%`}</span>
                  <span className="text-[10px] text-white/30 font-bold uppercase tracking-widest mt-1">{t('success')}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 mt-10">
                {successRateData.map((item) => (
                  <div key={item.name} className="flex justify-between items-center group">
                    <div className="flex items-center gap-4">
                      <div className="w-2.5 h-2.5 rounded-full ring-4 ring-white/5" style={{ backgroundColor: item.color }} />
                      <span className="text-sm font-semibold text-white/50 tracking-wide group-hover:text-white transition-colors">{item.name}</span>
                    </div>
                    <span className="text-sm font-bold text-white tracking-tight">{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Performance + Agent Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
        <div className="controlled-glass rounded-[24px] p-6 inner-highlight flex flex-col h-full">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-lg font-bold text-white tracking-tight">{t('weeklyPerformance')}</h2>
              <p className="text-xs text-white/30 mt-1 font-medium italic">{t('callsPerDay')}</p>
            </div>
            <div className="p-2 rounded-xl bg-white/5 border border-white/10">
              <TrendingUp className="h-4 w-4 text-white/40" />
            </div>
          </div>
          <div className="flex-1 min-h-[220px] w-full flex items-center justify-center">
            <p className="text-xs font-bold text-white/20 uppercase tracking-widest italic">No performance data available yet</p>
          </div>
        </div>

        <div className="controlled-glass rounded-[24px] p-6 inner-highlight flex flex-col h-full">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-lg font-bold text-white tracking-tight">{t('agentPerformance')}</h2>
              <p className="text-xs text-white/30 mt-1 font-medium italic">{t('individualStats')}</p>
            </div>
            <div className="p-2 rounded-xl bg-white/5 border border-white/10">
              <Activity className="h-4 w-4 text-white/40" />
            </div>
          </div>
          <div className="flex-1 space-y-3">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="p-3 rounded-xl animate-pulse">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-white/5" />
                    <div className="flex-1">
                      <div className="h-2.5 w-32 bg-white/10 rounded-full" />
                      <div className="h-2 w-16 bg-white/5 rounded-full mt-1.5" />
                    </div>
                    <div className="h-2.5 w-12 bg-white/10 rounded-full" />
                  </div>
                  <div className="h-1 w-full bg-white/5 rounded-full" />
                </div>
              ))
            ) : agentStats.length > 0 ? (
              agentStats.map((agent) => (
                <div key={agent.name} className="group p-3 hover:bg-white/5 rounded-xl transition-all border border-transparent hover:border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-500/10 border border-primary-500/20 flex items-center justify-center font-bold text-primary-400 text-xs">
                        {agent.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-white group-hover:text-primary-400 transition-colors uppercase tracking-tight">{agent.name}</h4>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={`w-1 h-1 rounded-full ${agent.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
                          <span className="text-[9px] font-bold text-white/20 uppercase tracking-widest">{agent.status}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs font-bold text-white">{agent.calls} <span className="text-[9px] text-white/20 uppercase font-bold tracking-widest ml-0.5">{t('calls')}</span></p>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center text-[9px] font-bold uppercase tracking-widest">
                      <span className="text-white/30 italic">{t('successRate')}</span>
                      <span className="text-primary-400">{agent.rate}%</span>
                    </div>
                    <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden p-[1px] border border-white/5">
                      <div
                        className="h-full bg-gradient-to-r from-primary-600 to-primary-400 rounded-full transition-all duration-1000 shadow-[0_0_6px_rgba(79,110,247,0.3)]"
                        style={{ width: `${agent.rate}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-white/20 italic text-center py-8">No agents yet</p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default Analytics;
